import { useEffect, useState } from "react";
import Popup from "../../components/Popup";
import SuspensePopup from "../../components/SuspensePopup";
import BenefitPreview from "../BenefitPreview";
import {Button, Pagination, Tag} from "omoplay";
import MiniMenu from "../form/MiniMenu";
import {deleteBenefit, listBenefit} from "../../api/benefit/benefit.api";
import GenericBoard from "../form/GenericBoard";
import {describeCodePool, usedPercent} from "../../libs/benefitCodePool";
import BenefitInfoPopup from "../form/benefit/BenefitInfoPopup";
import BenefitInfoButton from "../form/benefit/BenefitInfoButton";
import {displayBenefitName, isBlankName} from "../../libs/benefitName";

/**
 * '혜택 코드형' 한 건의 쿠폰코드 적재 현황 칸.
 *
 * 코드형이 아니면 **빈 칸**이다(문구를 넣으면 목록이 읽기 어려워진다).
 * 판정 규칙과 경고 기준은 `libs/benefitCodePool.js` 에 있고 테스트로 고정돼 있다.
 */
const CodePoolCell = ({benefit}) => {
  const status = describeCodePool(benefit)
  if (status === null) return <></>

  const tone = {
    ok:       {text: "text-gray-700", bar: "bg-blue-500",   sub: "text-gray-400"},
    low:      {text: "text-amber-700", bar: "bg-amber-500", sub: "text-amber-600"},
    empty:    {text: "text-red-600",  bar: "bg-red-500",    sub: "text-red-600"},
    unloaded: {text: "text-amber-700", bar: "bg-gray-200",  sub: "text-amber-600"},
    none:     {text: "text-gray-400", bar: "bg-gray-200",   sub: "text-gray-400"},
  }[status.tone]

  /*
    🔑 폭을 **130px 로 고정**한다(2026-09-10 2차 — 막대가 가로를 너무 넓게 먹었다).
       `w-full` 로 두면 `GenericBoard` 의 칸 폭(=아래 `columns` 의 `w-*`)만큼 늘어나므로
       칸이 아니라 **이 블록**에 폭을 준다. 칸 설정만 줄이면 다른 열이 밀려 배치가 흔들린다.
  */
  return (
      <div style={{width: "130px"}}>
        <div className={`text-xs font-semibold ${tone.text}`}>{status.title}</div>
        {status.loaded > 0 && (
            <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
              <div className={`h-full ${tone.bar}`} style={{width: `${usedPercent(status)}%`}} />
            </div>
        )}
        <div className={`text-[11px] mt-1 whitespace-nowrap ${tone.sub}`}>{status.detail}</div>
      </div>
  )
}

const BenefitList = () => {
  const [page, setPage] = useState(1);
  const [benefits, setBenefits] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [deleteBenefitId, setDeleteBenefitId] = useState(undefined);
  const [previewPopup, setPreviewPopup] = useState();
  // (i) 버튼으로 연 행. null 이면 닫힘.
  const [infoBenefit, setInfoBenefit] = useState(null)
  const [tableData, setTableData] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  const rowSize = 10

  useEffect(() => {
    listBenefit(page, rowSize).then(({data}) => {
      setBenefits(data.benefits)
      setTotalPages(data.count);
    })
  }, [page])

  function goEdit(benefitId) {
    window.location.href = (`/benefit/edit?id=${benefitId}`)
  }

  useEffect(() => {
    setTableData(benefits.map(benefit => {
      let badgeColor;
      if (benefit.status === "ACTIVE") {
        badgeColor = "success"
      } else {
        badgeColor = "default"
      }
      return [
        <div className="flex-align-center h-full"><Tag>{benefit.typeKor}</Tag></div>,
        // 소비자가 칸('미설정' 이 대부분)은 뺐다(2026-11-03 사용자 지시 — 고객사가 거의 쓰지 않는 값).
        // 대신 혜택명 **바로 오른쪽**에 (i) → 클릭하면 이미지·소비자가·원가 팝업, hover 로 ID.
        // 별도 칸으로 두면 (i) 가 중간에 떠 보여 어색하다(2026-11-03 2차 지시) → 혜택명 칸 안에 붙인다.
        <div className="flex-align-center h-full min-w-0">
          {/* 이름이 비면 (이름 없음) 플레이스홀더 — 글자가 없으면 클릭 면적이 0 이라 편집으로 못 들어간다(2026-11-03).
              새 저장은 서버·프론트가 막지만, 이미 저장된 빈 이름 혜택(운영 10건)을 위해 남긴다. */}
          <span className={`pointer truncate ${isBlankName(benefit.name) ? "text-gray-400 italic" : "text-primary"}`}
                onClick={() => goEdit(benefit.id)}>{displayBenefitName(benefit.name)}</span>
          <BenefitInfoButton benefitId={benefit.id} onClick={() => setInfoBenefit(benefit)} />
        </div>,
        // 쿠폰코드 적재 현황 — 혜택 코드형만 채워진다(2026-09-10 사용자 지시).
        <CodePoolCell benefit={benefit} />,
        <div className="flex-center-all h-full">
          <Tag color={badgeColor}>{benefit.statusKor}</Tag>
        </div>,
        // 날짜 하나만 있으면 생성일인지 수정일인지 알 수 없다 → 두 줄로 둘 다(2026-11-03 사용자 지시).
        <div className="flex-col justify-center h-full text-right text-xs text-gray-500 whitespace-nowrap">
          <div><span className="text-gray-400 mr-1">생성</span>{benefit.createdAt}</div>
          <div><span className="text-gray-400 mr-1">수정</span>{benefit.modifiedAt}</div>
        </div>,
        <div className="flex-center-all h-full">
          <MiniMenu
              id={benefit.id}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              onClickEdit={() => goEdit(benefit.id)}
              /*onClickDelete={() => setDeleteBenefitId(benefit.id)}*/
              disabledDelete={benefit.used}
          />
        </div>
      ]
    }))
  }, [benefits, openMenuId]);

  const handleDelete = () => {
    setDeleteBenefitId(undefined)
    deleteBenefit(deleteBenefitId).then(() => {
      window.location.reload()
    })
  };

  return (
    <>
      <div>
        <div className="w-full flex items-center justify-between">
          <div className="flex gap-4 items-center">
          </div>
          <div className="w-full flex justify-between gap-2 items-center">
            <div>
              총 <span className="text-primary font-bold">{totalPages.toLocaleString()}</span>개
            </div>
            <Button
                variants="primary"
                value="+ 혜택 추가"
                onClick={() => window.location.href = ("/benefit/edit")}
             />
          </div>
        </div>
        <div className="flex-col py-4">
          {/*
            열 순서 = 타입 · 혜택명(i) · **쿠폰코드 적재** · 상태 · 생성/수정일 · 메뉴
            (`GenericBoard` 는 헤더를 그리지 않는 카드형이라 각 칸이 스스로를 설명해야 한다.
             그래서 적재 칸에도 '사용 / 적재' 문구를 함께 넣는다.)
          */}
          <GenericBoard items={tableData} columns={[
            { align: 'left', width: 'w-32' },
            { align: 'left', flex: true },
            { align: 'left', width: 'w-[130px]' },
            { align: 'right', width: 'w-32' },
            { align: 'right', width: 'w-40' }
          ]} />
          <div className="flex justify-center" style={{marginTop: "24px"}}>
            <Pagination pageSize={rowSize} totalCount={totalPages} currentPage={page} onPaging={(page) => setPage(page)} />
          </div>
        </div>
      </div>
      <SuspensePopup
          title={"미리보기"}
          visible={previewPopup}
          onClose={() => setPreviewPopup(null)}
      >
        <BenefitPreview id={previewPopup} />
      </SuspensePopup>
      <BenefitInfoPopup benefit={infoBenefit} onClose={() => setInfoBenefit(null)} />
      {deleteBenefitId && (
        <Popup
          title={"정말 삭제하시겠습니까?"}
          buttons={[
            { label: "취소", onClick: () => setDeleteBenefitId(undefined) },
            { label: "확인", type: "info", onClick: () => handleDelete() },
          ]}
        />
      )}
    </>
  );
};
export default BenefitList;
